import { CalendarIcon } from 'lucide-react';
import {
  Button,
  Calendar,
  cn,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@scio3/ui';

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

/** Convert 24h hour to { h12, period } for display */
function to12Hour(h24: number): { h12: number; period: 'AM' | 'PM' } {
  return { h12: h24 % 12 || 12, period: h24 >= 12 ? 'PM' : 'AM' };
}

/** Convert 12h hour + period back to 24h for storage */
function to24Hour(h12: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

export interface DateTimePickerProps {
  /** ISO date string */
  value: string;
  /** Callback with ISO date string */
  onChange: (iso: string) => void;
  /** Field label displayed above the picker */
  label: string;
  /** Validation error message */
  error?: string;
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Label for the time section */
  timeLabel?: string;
  /** When true (default), read/write UTC hours. When false, use local time. */
  utc?: boolean;
}

export function DateTimePicker({
  value,
  onChange,
  label,
  error,
  placeholder,
  timeLabel,
  utc = true,
}: DateTimePickerProps) {
  const date = value ? new Date(value) : undefined;

  // Accessors that switch between UTC and local based on `utc` prop
  const getHours = (d: Date) => (utc ? d.getUTCHours() : d.getHours());
  const getMinutes = (d: Date) => (utc ? d.getUTCMinutes() : d.getMinutes());
  const getMonth = (d: Date) => (utc ? d.getUTCMonth() : d.getMonth());
  const getDate = (d: Date) => (utc ? d.getUTCDate() : d.getDate());
  const getFullYear = (d: Date) => (utc ? d.getUTCFullYear() : d.getFullYear());

  // react-day-picker compares dates using local timezone (date-fns isSameDay → startOfDay).
  // In UTC mode, a UTC midnight date (e.g. 2026-02-24T00:00:00Z) would highlight the wrong
  // day in negative-offset timezones. Convert to a local-equivalent date so the calendar
  // highlights the correct day.
  const calendarDate =
    utc && date ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) : date;

  const handleDateSelect = (day: Date | undefined) => {
    if (!day) return;
    const current = date ?? new Date();

    if (utc) {
      // react-day-picker gives a local-timezone Date for the clicked day.
      // Read local date parts and combine with current UTC time via Date.UTC.
      const d = new Date(
        Date.UTC(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          current.getUTCHours(),
          current.getUTCMinutes(),
          0,
          0,
        ),
      );
      onChange(d.toISOString());
    } else {
      day.setHours(current.getHours(), current.getMinutes(), 0, 0);
      onChange(day.toISOString());
    }
  };

  const handleTimeChange = (hours: number, minutes: number) => {
    const d = date ? new Date(date) : new Date();
    if (utc) {
      d.setUTCHours(hours, minutes, 0, 0);
    } else {
      d.setHours(hours, minutes, 0, 0);
    }
    onChange(d.toISOString());
  };

  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            aria-label={label}
            className={cn(
              'w-full justify-start border-input text-left text-sm font-normal',
              !date && 'text-muted-foreground',
              error && 'border-destructive',
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
            {date
              ? (() => {
                  const h = getHours(date);
                  const m = getMinutes(date);
                  const ampm = h >= 12 ? 'PM' : 'AM';
                  const h12 = h % 12 || 12;
                  const mm = String(getMonth(date) + 1).padStart(2, '0');
                  const dd = String(getDate(date)).padStart(2, '0');
                  const yy = String(getFullYear(date)).slice(-2);
                  return `${mm}/${dd}/${yy}, ${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                })()
              : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={calendarDate}
            defaultMonth={calendarDate}
            onSelect={handleDateSelect}
            initialFocus
          />
          <div className="border-t p-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{timeLabel}:</span>
              {(() => {
                const h24 = date ? getHours(date) : 0;
                const { h12, period } = to12Hour(h24);
                return (
                  <>
                    <Select
                      value={String(h12)}
                      onValueChange={(v) =>
                        handleTimeChange(to24Hour(Number(v), period), date ? getMinutes(date) : 0)
                      }
                    >
                      <SelectTrigger className="h-8 w-[60px] text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-48">
                          {HOURS_12.map((h) => (
                            <SelectItem key={h} value={String(h)}>
                              {h}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">:</span>
                    <Select
                      value={String(date ? getMinutes(date) : 0)}
                      onValueChange={(v) => handleTimeChange(h24, Number(v))}
                    >
                      <SelectTrigger className="h-8 w-[60px] text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MINUTES.map((m) => (
                          <SelectItem key={m} value={String(m)}>
                            {String(m).padStart(2, '0')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={period}
                      onValueChange={(v) =>
                        handleTimeChange(
                          to24Hour(h12, v as 'AM' | 'PM'),
                          date ? getMinutes(date) : 0,
                        )
                      }
                    >
                      <SelectTrigger className="h-8 w-[65px] text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AM">AM</SelectItem>
                        <SelectItem value="PM">PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                );
              })()}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
